'use client';

import { useEffect } from 'react';
import { Variants, motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useThemeColor } from '@/hooks/useThemeColor';
import ImageAdaptive from '@/components/ImageAdaptive';

interface SectionDecoratedTitleProps {
    title: string;
    subTitle?: string;
}

export default function SectionDecoratedTitle({ title, subTitle }: SectionDecoratedTitleProps) {
    const { scheme } = useThemeColor();

    const controls = useAnimation();
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.2,
    });

    useEffect(() => {
        if (inView) controls.start('visible');
    }, [inView, controls]);

    const titleVariants: Variants = {
        hidden: { opacity: 0, y: 40 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                delay: 0.5,
                duration: 0.6,
                ease: 'easeOut',
            },
        },
    };

    const imageVariants: Variants = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                delay: 1.0,
                duration: 0.6,
                ease: 'easeOut',
            },
        },
    };

    return (
        <div ref={ref} className="flex flex-col items-center w-full pb-2">
            <motion.div
                className={`flex flex-col justify-center items-center w-full select-none px-2`}
                initial="hidden"
                animate={controls}
                variants={titleVariants}
            >
                <span
                    className={`flex items-center font-montserrat font-black text-color-base tracking-widest text-xl md:text-2xl lg:text-[35px] whitespace-nowrap ${scheme?.text}`}
                >
                    {title}.
                </span>
                {subTitle && (
                    <span className="flex items-end font-corp font-bold text-color-base tracking-widest text-sm lg:text-[16px] md:mt-[4px] whitespace-nowrap">
                        {subTitle}
                    </span>
                )}
            </motion.div>
            <motion.div className="relative pt-4 pb-4" initial="hidden" animate={controls} variants={imageVariants}>
                <ImageAdaptive src={`/images/common/color-bar.png`} baseWidth={150} breakpoints={{ md: 200 }} />
                <motion.div
                    initial={{ x: '-100%' }}
                    animate={{ x: '350%' }}
                    transition={{
                        duration: 0.3,
                        repeat: Infinity,
                        repeatDelay: 5,
                        ease: 'easeInOut',
                    }}
                    className="absolute top-0 left-0 w-1/3 h-full bg-gradient-to-r from-transparent via-white/50 to-transparent transform rotate-12"
                />
            </motion.div>
        </div>
    );
}
